import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import { emissive } from '@/art/materials';
import type { CollisionWorld } from '@/world/collision';
import type { Faction } from '@/gameplay/GameState';

/**
 * Shooting: weapons, hitscan bullets, and visible feedback.
 *
 * Bullets are rays, not projectiles. At city scale and this tempo, players will not see the
 * difference, and it saves a simulation step per bullet. The ray is checked against the same
 * `CollisionWorld` used for movement, plus entity hit circles.
 *
 * Shot resolution (`resolveShot`) is independent from rendering, so it can be tested without
 * a scene. `Combat` adds tracers, muzzle flash, and impacts on top.
 */

export interface WeaponSpec {
  readonly name: string;
  readonly damage: number;
  /** Meters. Beyond this, the bullet hits nothing. */
  readonly range: number;
  readonly magazine: number;
  /** Seconds between shots. */
  readonly fireInterval: number;
  readonly reloadTime: number;
  /** Maximum angular deviation in radians, to either side. */
  readonly spread: number;
  /** Keeps firing while held, or fires one shot per click. */
  readonly automatic: boolean;
}

/** Luca's service weapon. One shot per click suits the tone: violence has weight. */
export const PISTOL: WeaponSpec = {
  name: 'Pistool',
  damage: 34,
  range: 45,
  magazine: 7,
  fireInterval: 0.22,
  reloadTime: 1.6,
  spread: 0.02,
  automatic: false,
};

/** Anything that can be hit. `Enemy` and `Player` conform to this. */
export interface CombatTarget {
  /** Position in the XZ plane. */
  readonly position: THREE.Vector2;
  /** Hit circle, roughly shoulder width. */
  readonly radius: number;
  readonly alive: boolean;
  readonly faction: Faction;
  damage(amount: number): void;
}

export type ShotOutcome = 'target' | 'wall' | 'miss';

export interface ShotResult {
  readonly outcome: ShotOutcome;
  /** Where the bullet ends: on an entity, against a wall, or at max range. */
  readonly point: THREE.Vector2;
  readonly distance: number;
  readonly target: CombatTarget | null;
}

/**
 * Weapon state: magazine, fire rate, and reload. One per shooter, so each actor owns their
 * own magazine.
 */
export class Weapon {
  ammo: number;
  #cooldown = 0;
  #reloadLeft = 0;

  constructor(readonly spec: WeaponSpec) {
    this.ammo = spec.magazine;
  }

  get reloading(): boolean {
    return this.#reloadLeft > 0;
  }

  get empty(): boolean {
    return this.ammo === 0;
  }

  /** Ready to fire: loaded, not reloading, cooldown elapsed. */
  get ready(): boolean {
    return this.ammo > 0 && this.#reloadLeft <= 0 && this.#cooldown <= 0;
  }

  update(dt: number): void {
    // Repeated dt subtraction leaves tiny leftovers. Without this threshold, reloads can
    // remain "almost done" forever.
    const EPSILON = 1e-6;
    if (this.#cooldown > 0) {
      this.#cooldown -= dt;
      if (this.#cooldown < EPSILON) this.#cooldown = 0;
    }
    if (this.#reloadLeft > 0) {
      this.#reloadLeft -= dt;
      if (this.#reloadLeft < EPSILON) {
        this.#reloadLeft = 0;
        this.ammo = this.spec.magazine;
      }
    }
  }

  /** Fire one bullet if allowed. Returns whether a shot actually happened. */
  pullTrigger(): boolean {
    if (!this.ready) return false;
    this.ammo--;
    this.#cooldown = this.spec.fireInterval;
    return true;
  }

  /** Start reloading. No-op when the magazine is already full or reload is in progress. */
  reload(): void {
    if (this.reloading || this.ammo === this.spec.magazine) return;
    this.#reloadLeft = this.spec.reloadTime;
  }

  reset(): void {
    this.ammo = this.spec.magazine;
    this.#cooldown = 0;
    this.#reloadLeft = 0;
  }
}

/** Distance along the ray to the circle, or `null` when the ray misses it. */
function rayCircleDistance(
  originX: number,
  originZ: number,
  dirX: number,
  dirZ: number,
  centreX: number,
  centreZ: number,
  radius: number,
): number | null {
  const mx = originX - centreX;
  const mz = originZ - centreZ;
  const b = mx * dirX + mz * dirZ;
  const c = mx * mx + mz * mz - radius * radius;
  if (c > 0 && b > 0) return null; // outside the circle and pointing away from it
  const discriminant = b * b - c;
  if (discriminant < 0) return null;
  return Math.max(0, -b - Math.sqrt(discriminant));
}

/**
 * What does this bullet hit? Pure function: no scene, no time, no randomness.
 *
 * The closest hit wins. A wall between shooter and target therefore protects the target
 * automatically; no separate visibility check is needed.
 */
export function resolveShot(
  origin: THREE.Vector2,
  direction: THREE.Vector2,
  range: number,
  collision: CollisionWorld,
  targets: readonly CombatTarget[],
  shooter: CombatTarget | null = null,
): ShotResult {
  const length = Math.hypot(direction.x, direction.y);
  if (length === 0) {
    return { outcome: 'miss', point: origin.clone(), distance: 0, target: null };
  }
  const dirX = direction.x / length;
  const dirZ = direction.y / length;
  const far = new THREE.Vector2(origin.x + dirX * range, origin.y + dirZ * range);

  let bestDistance = range;
  let bestTarget: CombatTarget | null = null;

  for (const candidate of targets) {
    if (candidate === shooter || !candidate.alive) continue;
    if (shooter && candidate.faction === shooter.faction) continue;
    const hit = rayCircleDistance(
      origin.x,
      origin.y,
      dirX,
      dirZ,
      candidate.position.x,
      candidate.position.y,
      candidate.radius,
    );
    if (hit !== null && hit < bestDistance) {
      bestDistance = hit;
      bestTarget = candidate;
    }
  }

  // `raycast` geeft sinds de slab-test het exacte intreepunt terug; het bijschaven met zeven
  // extra raycasts dat hier stond is daarmee overbodig geworden.
  const wall = collision.raycast(origin, far);
  if (wall) {
    const wallDistance = Math.hypot(wall.x - origin.x, wall.y - origin.y);
    if (wallDistance <= bestDistance) {
      return { outcome: 'wall', point: wall, distance: wallDistance, target: null };
    }
  }

  if (bestTarget) {
    return {
      outcome: 'target',
      point: new THREE.Vector2(origin.x + dirX * bestDistance, origin.y + dirZ * bestDistance),
      distance: bestDistance,
      target: bestTarget,
    };
  }

  return { outcome: 'miss', point: far, distance: range, target: null };
}

/** Height where bullets start and impact. Matches the aim plane in `Input`. */
const BULLET_HEIGHT = 1.2;
const TRACER_LIFETIME = 0.05;
const IMPACT_LIFETIME = 0.16;
const FLASH_LIFETIME = 0.05;
const TRACER_POOL = 16;
const IMPACT_POOL = 12;

interface Effect {
  readonly object: THREE.Object3D;
  life: number;
}

/**
 * Shooting system with rendering.
 *
 * Effects come from fixed pools. Gunfights can use dozens per second, and allocating a mesh
 * per shot would make the garbage collector stutter.
 */
export class Combat {
  readonly #collision: CollisionWorld;
  readonly #targets: CombatTarget[] = [];
  readonly #random: () => number;

  readonly #group = new THREE.Group();
  readonly #tracers: Effect[] = [];
  readonly #impacts: Effect[] = [];
  readonly #flash: THREE.PointLight;
  #flashLife = 0;

  constructor(scene: THREE.Scene, collision: CollisionWorld, random: () => number = Math.random) {
    this.#collision = collision;
    this.#random = random;

    this.#group.name = 'NA_Combat_Effects';
    scene.add(this.#group);

    // One shared muzzle flash: it is on for only 50 ms, so two simultaneous shooters do not
    // read differently from one. This saves lights within the twelve-light budget.
    this.#flash = new THREE.PointLight(0xffd9a0, 0, 9, 2);
    this.#flash.visible = false;
    this.#group.add(this.#flash);

    const tracerGeometry = new THREE.BoxGeometry(0.035, 0.035, 1);
    for (let i = 0; i < TRACER_POOL; i++) {
      const mesh = new THREE.Mesh(tracerGeometry, emissive('cream', 1.4));
      mesh.visible = false;
      mesh.frustumCulled = false;
      this.#group.add(mesh);
      this.#tracers.push({ object: mesh, life: 0 });
    }

    const impactGeometry = new THREE.SphereGeometry(0.09, 5, 4);
    for (let i = 0; i < IMPACT_POOL; i++) {
      const mesh = new THREE.Mesh(impactGeometry, emissive('sodiumAmber', 1.6));
      mesh.visible = false;
      this.#group.add(mesh);
      this.#impacts.push({ object: mesh, life: 0 });
    }
  }

  addTarget(target: CombatTarget): void {
    if (!this.#targets.includes(target)) this.#targets.push(target);
  }

  removeTarget(target: CombatTarget): void {
    const index = this.#targets.indexOf(target);
    if (index >= 0) this.#targets.splice(index, 1);
  }

  get targets(): readonly CombatTarget[] {
    return this.#targets;
  }

  /**
   * Fire one bullet from `origin` toward `aim`. The shooter has already consumed ammo; this
   * method only resolves and visualizes the bullet path.
   */
  fire(
    origin: THREE.Vector2,
    aim: THREE.Vector2,
    spec: WeaponSpec,
    shooter: CombatTarget | null = null,
  ): ShotResult {
    const direction = new THREE.Vector2(aim.x - origin.x, aim.y - origin.y);
    if (direction.lengthSq() === 0) direction.set(0, 1);

    // Spread is angular, so it grows with distance.
    const angle = (this.#random() * 2 - 1) * spec.spread;
    const sin = Math.sin(angle);
    const cos = Math.cos(angle);
    direction.set(direction.x * cos - direction.y * sin, direction.x * sin + direction.y * cos);

    const result = resolveShot(origin, direction, spec.range, this.#collision, this.#targets, shooter);
    if (result.outcome === 'target' && result.target) result.target.damage(spec.damage);

    bus.emit('combat:shot', {
      x: origin.x,
      z: origin.y,
      weapon: spec.name,
      faction: shooter?.faction ?? 'neutral',
    });
    if (result.outcome !== 'miss') {
      bus.emit('combat:impact', {
        x: result.point.x,
        z: result.point.y,
        outcome: result.outcome,
      });
    }

    this.#showTracer(origin, result.point);
    this.#showFlash(origin);
    if (result.outcome !== 'miss') this.#showImpact(result.point);
    return result;
  }

  /** Decay active effects. Called from the fixed simulation step. */
  update(dt: number): void {
    for (const effect of this.#tracers) {
      if (effect.life <= 0) continue;
      effect.life -= dt;
      if (effect.life <= 0) effect.object.visible = false;
    }
    for (const effect of this.#impacts) {
      if (effect.life <= 0) continue;
      effect.life -= dt;
      const fade = Math.max(0, effect.life / IMPACT_LIFETIME);
      effect.object.scale.setScalar(fade);
      if (effect.life <= 0) effect.object.visible = false;
    }
    if (this.#flashLife > 0) {
      this.#flashLife -= dt;
      this.#flash.intensity = Math.max(0, this.#flashLife / FLASH_LIFETIME) * 6;
      if (this.#flashLife <= 0) this.#flash.visible = false;
    }
  }

  dispose(): void {
    this.#group.removeFromParent();
    for (const effect of [...this.#tracers, ...this.#impacts]) {
      (effect.object as THREE.Mesh).geometry.dispose();
    }
  }

  #claim(pool: Effect[], lifetime: number): Effect {
    // Reuse the oldest active effect when the pool is full. A tracer living one frame less is
    // invisible; a missing tracer is not.
    let chosen = pool[0]!;
    for (const effect of pool) {
      if (effect.life <= 0) {
        chosen = effect;
        break;
      }
      if (effect.life < chosen.life) chosen = effect;
    }
    chosen.life = lifetime;
    chosen.object.visible = true;
    return chosen;
  }

  #showTracer(from: THREE.Vector2, to: THREE.Vector2): void {
    const effect = this.#claim(this.#tracers, TRACER_LIFETIME);
    const dx = to.x - from.x;
    const dz = to.y - from.y;
    const length = Math.hypot(dx, dz);
    effect.object.position.set(from.x + dx / 2, BULLET_HEIGHT, from.y + dz / 2);
    effect.object.rotation.set(0, Math.atan2(dx, dz), 0);
    effect.object.scale.set(1, 1, Math.max(0.01, length));
  }

  #showImpact(at: THREE.Vector2): void {
    const effect = this.#claim(this.#impacts, IMPACT_LIFETIME);
    effect.object.position.set(at.x, BULLET_HEIGHT, at.y);
    effect.object.scale.setScalar(1);
  }

  #showFlash(at: THREE.Vector2): void {
    this.#flash.position.set(at.x, BULLET_HEIGHT, at.y);
    this.#flash.visible = true;
    this.#flashLife = FLASH_LIFETIME;
    this.#flash.intensity = 6;
  }
}
